/**
 * Map SiteGlue Intern / Associate / Professional plans → HireRecommendationsSelection presets
 * and expose which features are gated above a given plan.
 */

import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import {
  INCLUDED_FREE_CHAT_CONVERSATIONS,
  normalizeRecommendationsSelection,
  type HireRecommendationsSelection,
} from "~/lib/marketplace/hire-recommendations-pricing";
import type { SchedulingLevel } from "~/lib/siteglue/consultant-onboarding";

export type PlanFeatureKey =
  | "phoneCalls"
  | "browserCalls"
  | "dedicatedPhone"
  | "dedicatedEmail"
  | "email"
  | "textMessages"
  | "whatsapp"
  | "messenger"
  | "videoCalls"
  | "appointmentsEnabled"
  | "emrIntegrationEnabled"
  | "agentTrainingAdvanced"
  | "multiLocation"
  | "extraTeamMembers";

/**
 * Infer the SiteGlue Intern / Associate / Professional plan that covers a hire
 * configuration (channels, org size). Used for pricing display and Trish prompts.
 */
export function inferAiEmployeePlanFromSelection(
  selection: HireRecommendationsSelection,
): Exclude<AiEmployeePlanId, "enterprise"> {
  const hasAdvancedChannels =
    selection.textMessages ||
    selection.whatsapp ||
    selection.messenger ||
    selection.videoCalls ||
    selection.emrIntegrationEnabled;
  // Voice (WebRTC) is included on Intern — do not bump plan for it alone.
  const hasPhoneOrLightScheduling =
    selection.phoneCalls ||
    selection.dedicatedPhone ||
    selection.appointmentsEnabled;

  if (hasAdvancedChannels || selection.locationCount >= 3 || selection.teamMemberCount >= 6) {
    return "professional";
  }
  if (
    hasPhoneOrLightScheduling ||
    selection.locationCount >= 2 ||
    selection.teamMemberCount >= 2
  ) {
    return "associate";
  }
  return "intern";
}

/** Features allowed at or below each paid/free plan (enterprise = all). */
const PLAN_FEATURE_CEILING: Record<
  Exclude<AiEmployeePlanId, "enterprise">,
  PlanFeatureKey[]
> = {
  intern: ["browserCalls"],
  associate: [
    "phoneCalls",
    "browserCalls",
    "dedicatedPhone",
    "dedicatedEmail",
    "email",
    "appointmentsEnabled",
    "extraTeamMembers",
  ],
  professional: [
    "phoneCalls",
    "browserCalls",
    "dedicatedPhone",
    "dedicatedEmail",
    "email",
    "textMessages",
    "whatsapp",
    "messenger",
    "videoCalls",
    "appointmentsEnabled",
    "emrIntegrationEnabled",
    "agentTrainingAdvanced",
    "multiLocation",
    "extraTeamMembers",
  ],
};

export function planAllowsFeature(
  planId: AiEmployeePlanId,
  feature: PlanFeatureKey,
): boolean {
  if (planId === "enterprise") return true;
  return PLAN_FEATURE_CEILING[planId].includes(feature);
}

export function recommendationsFromAiEmployeePlan(
  planId: AiEmployeePlanId,
  opts?: {
    locationCount?: number;
    teamMemberCount?: number;
    schedulingLevel?: SchedulingLevel;
    schedulingIntegrationId?: HireRecommendationsSelection["schedulingIntegrationId"];
    emrIntegrationId?: HireRecommendationsSelection["emrIntegrationId"];
  },
): HireRecommendationsSelection {
  const base = normalizeRecommendationsSelection({
    liveChat: true,
    chatConversations: INCLUDED_FREE_CHAT_CONVERSATIONS,
    locationCount: 1,
    departmentCount: 1,
    teamMemberCount: 1,
    teamHandoffEnabled: true,
  });
  const locations = Math.max(1, opts?.locationCount ?? 1);
  const team = Math.max(1, opts?.teamMemberCount ?? 1);
  const level = opts?.schedulingLevel ?? "none";
  const wantsAppointments = level !== "none";

  if (planId === "intern") {
    return {
      ...base,
      browserCalls: true,
      locationCount: 1,
      teamMemberCount: 1,
      chatConversations: INCLUDED_FREE_CHAT_CONVERSATIONS,
    };
  }

  if (planId === "associate") {
    return {
      ...base,
      phoneCalls: true,
      browserCalls: true,
      dedicatedPhone: true,
      dedicatedEmail: true,
      email: true,
      appointmentsEnabled: wantsAppointments,
      schedulingIntegrationId:
        wantsAppointments && opts?.schedulingIntegrationId
          ? opts.schedulingIntegrationId
          : wantsAppointments
            ? "google_calendar"
            : "none",
      locationCount: Math.min(locations, 2),
      teamMemberCount: Math.min(Math.max(team, 2), 5),
      teamHandoffEnabled: true,
      chatConversations: 2000,
      phoneCallMinutes: 100,
    };
  }

  // professional + enterprise presets (enterprise unlocks everything; pricing custom)
  return {
    ...base,
    phoneCalls: true,
    browserCalls: true,
    dedicatedPhone: true,
    dedicatedEmail: true,
    email: true,
    textMessages: true,
    whatsapp: true,
    messenger: true,
    videoCalls: true,
    appointmentsEnabled: wantsAppointments,
    schedulingIntegrationId:
      wantsAppointments && opts?.schedulingIntegrationId
        ? opts.schedulingIntegrationId
        : wantsAppointments
          ? "calendly"
          : "none",
    emrIntegrationEnabled: Boolean(
      wantsAppointments && opts?.emrIntegrationId && opts.emrIntegrationId !== "none",
    ),
    emrIntegrationId: opts?.emrIntegrationId ?? "none",
    agentTrainingAdvanced: true,
    locationCount: Math.max(locations, planId === "enterprise" ? locations : Math.min(locations, 5)),
    teamMemberCount: Math.max(team, 3),
    teamHandoffEnabled: true,
    chatConversations: planId === "enterprise" ? 10000 : 10000,
    smsCount: 500,
    phoneCallMinutes: 300,
    videoCallMinutes: 60,
  };
}

export function recommendPlanFromConsultant(input: {
  channels: string[];
  locationCount: number;
  teamMemberCount: number;
  schedulingLevel: SchedulingLevel;
  needsEmr: boolean;
  estimatedConversations?: number;
}): { planId: AiEmployeePlanId; reasons: string[] } {
  const channels = input.channels.map((c) => c.toLowerCase());
  const needsPhone = channels.some((c) => c.includes("phone"));
  const needsSms = channels.some((c) => c.includes("sms") || c.includes("text"));
  const needsWhatsapp = channels.some((c) => c.includes("whatsapp"));
  const needsVideo = channels.some((c) => c.includes("video"));
  const advancedScheduling =
    input.schedulingLevel === "advanced" || input.schedulingLevel === "enterprise";
  const multiLocation = input.locationCount >= 3;
  const manyTeam = input.teamMemberCount >= 6;

  const reasons: string[] = [];

  if (
    multiLocation ||
    manyTeam ||
    input.schedulingLevel === "enterprise" ||
    (input.needsEmr && input.locationCount >= 2)
  ) {
    reasons.push("Multiple locations, complex scheduling, or EMR needs fit Enterprise.");
    reasons.push("Custom routing, volume, and integration support may be required.");
    return { planId: "enterprise", reasons };
  }

  if (
    needsSms ||
    needsWhatsapp ||
    needsVideo ||
    advancedScheduling ||
    input.needsEmr ||
    input.locationCount >= 2
  ) {
    if (needsPhone) reasons.push("Phone coverage is required.");
    if (needsSms || needsWhatsapp) reasons.push("SMS / messaging channels are required.");
    if (advancedScheduling || input.needsEmr) {
      reasons.push("Advanced or EMR-connected scheduling is recommended.");
    }
    if (input.locationCount >= 2) reasons.push("More than one location needs coverage.");
    reasons.push("Professional includes full-channel coverage with humans in the loop.");
    return { planId: "professional", reasons };
  }

  if (needsPhone || input.schedulingLevel === "basic" || input.schedulingLevel === "appointment_request") {
    reasons.push("Phone and/or light scheduling needs exceed free Live Chat.");
    reasons.push("Associate adds dedicated phone/email with Human-in-the-Loop™ takeover.");
    return { planId: "associate", reasons };
  }

  reasons.push("Website chat alone can start capturing conversations on the free Intern plan.");
  reasons.push("You can upgrade channels later without rebuilding your AI employee.");
  return { planId: "intern", reasons };
}

export function adjacentPlanComparison(planId: AiEmployeePlanId): {
  lower: AiEmployeePlanId | null;
  higher: AiEmployeePlanId | null;
} {
  const order: AiEmployeePlanId[] = ["intern", "associate", "professional", "enterprise"];
  const i = order.indexOf(planId);
  return {
    lower: i > 0 ? order[i - 1]! : null,
    higher: i >= 0 && i < order.length - 1 ? order[i + 1]! : null,
  };
}

const SITEGLUE_MARKETING_PLAN_CYCLE: Exclude<AiEmployeePlanId, "enterprise">[] = [
  "intern",
  "associate",
  "professional",
];

/** Next plan in Intern → Associate → Professional → Intern cycle (agent profile sidebar). */
export function siteGluePlanViewTarget(
  currentPlan: AiEmployeePlanId,
): AiEmployeePlanId {
  if (currentPlan === "enterprise") return "professional";
  const idx = SITEGLUE_MARKETING_PLAN_CYCLE.indexOf(
    currentPlan as (typeof SITEGLUE_MARKETING_PLAN_CYCLE)[number],
  );
  if (idx === -1) return "intern";
  return SITEGLUE_MARKETING_PLAN_CYCLE[(idx + 1) % SITEGLUE_MARKETING_PLAN_CYCLE.length]!;
}

/** Merge plan channel presets with org structure the user already configured. */
export function mergeSelectionWithInferredPlan(
  selection: HireRecommendationsSelection,
  planId: Exclude<AiEmployeePlanId, "enterprise">,
): HireRecommendationsSelection {
  const schedulingLevel: SchedulingLevel = selection.emrIntegrationEnabled
    ? "advanced"
    : selection.appointmentsEnabled
      ? "appointment_request"
      : "none";

  const preset = recommendationsFromAiEmployeePlan(planId, {
    locationCount: selection.locationCount,
    teamMemberCount: selection.teamMemberCount,
    schedulingLevel,
    schedulingIntegrationId:
      selection.schedulingIntegrationId !== "none"
        ? selection.schedulingIntegrationId
        : undefined,
    emrIntegrationId:
      selection.emrIntegrationId !== "none" ? selection.emrIntegrationId : undefined,
  });

  return normalizeRecommendationsSelection({
    ...preset,
    locationCount: selection.locationCount,
    departmentCount: selection.departmentCount,
    teamMemberCount: selection.teamMemberCount,
    locationList: selection.locationList,
    locationDetails: selection.locationDetails,
    departmentList: selection.departmentList,
    departmentDetails: selection.departmentDetails,
    extraTeamMembers: selection.extraTeamMembers,
    teamMembersLabel: selection.teamMembersLabel,
    teamHandoffCtaClusters: selection.teamHandoffCtaClusters,
    teamHandoffEnabled: selection.teamHandoffEnabled,
    welcomeVideo: selection.welcomeVideo,
    welcomeVideoMinutes: selection.welcomeVideoMinutes,
  });
}
